import React from "react";
import { Chip } from "@mui/material";
import PropTypes from "prop-types";

/**
 * CChip - A wrapper for MUI Chip with additional functionality
 * 
 * @param {Object} props
 * @param {string} props.label - The content of the chip
 * @param {string} props.color - The color of the chip (default, primary, secondary, error, info, success, warning)
 * @param {string} props.variant - The variant to use (outlined or filled)
 * @param {boolean} props.clickable - If true, the chip will be clickable
 * @param {boolean} props.disabled - If true, the chip will be disabled
 * @param {function} props.onDelete - Callback fired when the delete icon is clicked
 * @param {function} props.onClick - Callback fired when the chip is clicked
 * @param {React.ReactNode} props.icon - Icon element placed before the label
 * @param {React.ReactNode} props.deleteIcon - Custom delete icon element
 * @param {string} props.size - The size of the chip (small or medium)
 * @param {Object} props.sx - Additional styling for the chip
 * @param {Object} props.theme - Theme object for styling
 * @returns {JSX.Element}
 */
const CChip = ({
  label,
  color = "default",
  variant = "filled",
  clickable = false,
  disabled = false,
  onDelete,
  onClick,
  icon,
  deleteIcon,
  size = "medium",
  sx = {},
  theme,
  ...rest
}) => {
  // Default styles based on theme (if provided)
  const defaultSx = theme
    ? {
        borderRadius: theme.shape.borderRadius,
        fontSize: theme.typography.pxToRem(12),
        fontWeight: 500,
        letterSpacing: '0.5px',
        ...sx
      }
    : { ...sx };

  return (
    <Chip
      label={label}
      color={color}
      variant={variant}
      clickable={clickable}
      disabled={disabled}
      onDelete={onDelete}
      onClick={onClick}
      icon={icon}
      deleteIcon={deleteIcon}
      size={size}
      sx={defaultSx}
      {...rest}
    />
  );
};

CChip.propTypes = {
  label: PropTypes.node.isRequired,
  color: PropTypes.oneOf(["default", "primary", "secondary", "error", "info", "success", "warning"]),
  variant: PropTypes.oneOf(["filled", "outlined"]),
  clickable: PropTypes.bool,
  disabled: PropTypes.bool,
  onDelete: PropTypes.func,
  onClick: PropTypes.func,
  icon: PropTypes.node,
  deleteIcon: PropTypes.node,
  size: PropTypes.oneOf(["small", "medium"]),
  sx: PropTypes.object,
  theme: PropTypes.object,
};

export default CChip;